/**
 * offlineStore.js
 *
 * IndexedDB-backed queue for videos and catches that could not be synced
 * while the device was offline (or in forced offline mode).
 *
 * DB: the99_offline   v1
 * Stores:
 *   pendingVideos  – { id, certId, blob, metadata, queuedAt }
 *                    (large clips instead carry a filesystem reference:
 *                     { id, certId, blob:null, filePath, fileDirectory,
 *                       fileSize, metadata, queuedAt })
 *   pendingCatches – { id, catchData, conditionsSnapshot, location, userId,
 *                      sessionId, queuedAt }
 */

const DB_NAME = "the99_offline";
const DB_VERSION = 2;
const STORE_VIDEOS = "pendingVideos";
const STORE_CATCHES = "pendingCatches";
const STORE_GEOFENCE_ALERTS = "pendingGeofenceAlerts";

/**
 * Broadcast that the offline queue changed so live consumers (useOfflineSync)
 * can re-read counts/items immediately instead of waiting for the next mount
 * or sync. Fired after every add/remove.
 */
export const QUEUE_CHANGED_EVENT = "the99:offline-queue-changed";
function notifyQueueChanged() {
  try {
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
    }
  } catch {
    /* best-effort */
  }
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_VIDEOS)) {
        db.createObjectStore(STORE_VIDEOS, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CATCHES)) {
        db.createObjectStore(STORE_CATCHES, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_GEOFENCE_ALERTS)) {
        db.createObjectStore(STORE_GEOFENCE_ALERTS, { keyPath: "id", autoIncrement: true });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Videos ──────────────────────────────────────────────────────────────────

/** Queue a video blob for later upload. Returns the IDB record id. */
export async function queuePendingVideo({ certId, blob, metadata }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readwrite");
    const req = tx.objectStore(STORE_VIDEOS).add({
      certId,
      blob,
      metadata,
      queuedAt: Date.now(),
    });
    req.onsuccess = () => { notifyQueueChanged(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Queue a large video that has been staged on the device filesystem (native).
 * Instead of a Blob we store a file reference so multi-GB clips never live in
 * IndexedDB / the JS heap. `file` = { path, directory, size } from
 * videoFileStore. Returns the IDB record id.
 */
export async function queuePendingVideoFile({ certId, file, metadata }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readwrite");
    const req = tx.objectStore(STORE_VIDEOS).add({
      certId,
      blob: null,
      filePath: file?.path || null,
      fileDirectory: file?.directory || "DATA",
      fileSize: file?.size || 0,
      metadata,
      queuedAt: Date.now(),
    });
    req.onsuccess = () => { notifyQueueChanged(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

/** Return all queued video records. */
export async function getAllPendingVideos() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readonly");
    const req = tx.objectStore(STORE_VIDEOS).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Return a single queued video record by its IDB id (or null). */
export async function getPendingVideoById(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readonly");
    const req = tx.objectStore(STORE_VIDEOS).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

/** Remove a video from the queue by its IDB record id. */
export async function removePendingVideo(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readwrite");
    const req = tx.objectStore(STORE_VIDEOS).delete(id);
    req.onsuccess = () => { notifyQueueChanged(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

/** Return the count of queued videos. */
export async function getPendingVideoCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_VIDEOS, "readonly");
    const req = tx.objectStore(STORE_VIDEOS).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Catches ──────────────────────────────────────────────────────────────────

/** Queue a catch for later Firestore write. Returns the IDB record id. */
export async function queuePendingCatch({ userId, sessionId, catchData, conditionsSnapshot, location, tournament }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CATCHES, "readwrite");
    const req = tx.objectStore(STORE_CATCHES).add({
      userId,
      sessionId,
      catchData,
      conditionsSnapshot: conditionsSnapshot ?? null,
      location: location ?? null,
      tournament: tournament ?? null,
      queuedAt: Date.now(),
    });
    req.onsuccess = () => { notifyQueueChanged(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

/** Return all queued catch records. */
export async function getAllPendingCatches() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CATCHES, "readonly");
    const req = tx.objectStore(STORE_CATCHES).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Remove a catch from the queue by its IDB record id. */
export async function removePendingCatch(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CATCHES, "readwrite");
    const req = tx.objectStore(STORE_CATCHES).delete(id);
    req.onsuccess = () => { notifyQueueChanged(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

/** Return the count of queued catches. */
export async function getPendingCatchCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CATCHES, "readonly");
    const req = tx.objectStore(STORE_CATCHES).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Geofence Alerts ─────────────────────────────────────────────────────────

/** Queue a geofence alert payload for later Firestore write. */
export async function queuePendingGeofenceAlert(payload) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_GEOFENCE_ALERTS, "readwrite");
    const req = tx.objectStore(STORE_GEOFENCE_ALERTS).add({
      ...payload,
      queuedAt: Date.now(),
    });
    req.onsuccess = () => { notifyQueueChanged(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

/** Return all queued geofence alerts. */
export async function getAllPendingGeofenceAlerts() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_GEOFENCE_ALERTS, "readonly");
    const req = tx.objectStore(STORE_GEOFENCE_ALERTS).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Remove a geofence alert from the queue by id. */
export async function removePendingGeofenceAlert(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_GEOFENCE_ALERTS, "readwrite");
    const req = tx.objectStore(STORE_GEOFENCE_ALERTS).delete(id);
    req.onsuccess = () => { notifyQueueChanged(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

/** Return the count of queued geofence alerts. */
export async function getPendingGeofenceAlertCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_GEOFENCE_ALERTS, "readonly");
    const req = tx.objectStore(STORE_GEOFENCE_ALERTS).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
